/**
 * The charts of the graphical 16 Oct sections (Rusty, 19 Oct: "I want it all
 * graphical"), drawn like the top of the report: the same colours, thin marks,
 * every mark clickable (it opens the tickets behind it), a native hover title
 * on each mark, and the keyboard reaching every number through buttons.
 *
 * Each chart sits in a `ChartCard`, whose "Data table" toggle swaps the chart
 * for its numbers (the text alternative), and its "Export" menu (28 Oct) saves
 * the chart as a picture or its numbers as CSV; the full tables live in `Details`
 * ("Show details"), closed by default and left out of the PDF.
 */

import { Icon, Table2 } from "@norican/icons";
import { Button, cn, Collapsible, MiniSparkline, Toggle, Typography } from "@norican/react";
import { type ReactNode, useRef, useState } from "react";

import { useT } from "@/i18n";

import { Key, type Part } from "./insights-charts";
import { ExportMenu } from "./insights-export";
import { AGE_COLORS, C } from "./insights-helpers";
import { Definition } from "./insights-parts";
import {
  ageBucket,
  type DwellLike,
  dwellMax,
  flowLayout,
  type FlowPath,
  niceMax,
  widthPct,
} from "./insights-viz-helpers";

/** The lighter partner of the request blue: last week next to this week. */
export const LAST_WEEK = "#a5d8ff";
/** The middle half of the time-at-team spread, behind the median bar. */
const SPREAD = "#d0bfff";

// ── Frame: title, ⓘ, the data-table toggle ─────────────────────────────────

export interface ChartTable {
  columns: string[];
  rows: ReactNode[][];
}

/**
 * A chart with its title, definition and a "Data table" toggle that shows
 * the chart's numbers instead (the text alternative of every chart).
 */
export function ChartCard({
  title,
  definition,
  table,
  aside,
  className,
  children,
}: {
  title: string;
  definition: string;
  table: ChartTable;
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const { t } = useT("common");
  const [asTable, setAsTable] = useState(false);
  const ref = useRef<HTMLElement>(null);
  return (
    <figure ref={ref} className={cn("m-0 flex min-w-0 break-inside-avoid flex-col", className)}>
      <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <figcaption className="flex items-center gap-1">
          <h3 className="text-[13.5px] font-semibold">{title}</h3>
          <Definition text={definition} label={title} />
        </figcaption>
        {aside ? <div className="text-fg-muted text-[12px]">{aside}</div> : null}
        <Toggle
          size="sm"
          variant="ghost"
          className="ms-auto"
          pressed={asTable}
          onPressedChange={setAsTable}
          aria-label={t("leadershipReport.live.viz.dataTableOf", { name: title })}
          data-print-hide
        >
          <Icon icon={Table2} size="sm" />
          <span className="text-[12px]">{t("leadershipReport.live.viz.dataTable")}</span>
        </Toggle>
        <ExportMenu title={title} target={ref} table={table} />
      </div>
      {asTable ? <DataTable caption={title} table={table} /> : children}
    </figure>
  );
}

function DataTable({ caption, table }: { caption: string; table: ChartTable }) {
  return (
    <div className="border-border max-h-[420px] overflow-auto rounded-lg border">
      <table className="w-full text-[12.5px]">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-bg-subtle sticky top-0">
          <tr>
            {table.columns.map((c, i) => (
              <th
                key={c}
                scope="col"
                className={cn("px-2.5 py-1.5 font-semibold", i === 0 ? "text-start" : "text-end")}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r} className="border-border-subtle border-t">
              {row.map((cell, i) =>
                i === 0 ? (
                  <th key={i} scope="row" className="px-2.5 py-1 text-start font-normal">
                    {cell}
                  </th>
                ) : (
                  <td key={i} className="px-2.5 py-1 text-end tabular-nums">
                    {cell}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** "Show details": the section's full tables (search, filters, CSV), closed by default. */
export function Details({ children }: { children: ReactNode }) {
  const { t } = useT("common");
  const [open, setOpen] = useState(false);
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className="mt-4" data-print-hide>
      <Collapsible.Trigger render={<Button variant="outline" tone="neutral" size="sm" />}>
        {open
          ? t("leadershipReport.live.viz.hideDetails")
          : t("leadershipReport.live.viz.showDetails")}
      </Collapsible.Trigger>
      <Collapsible.Panel>
        <div className="flex flex-col gap-5 pt-3">{children}</div>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

/** A colour square and a label, for the legends above the bar charts. */
export function Swatch({
  color,
  label,
  shape = "square",
}: {
  color: string;
  label: string;
  shape?: "square" | "line" | "diamond";
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {shape === "line" ? (
        <i
          className="inline-block h-3 w-0 border-s-2 border-dashed"
          style={{ borderColor: color }}
          aria-hidden
        />
      ) : shape === "diamond" ? (
        <i className="inline-block h-2 w-2 rotate-45" style={{ background: color }} aria-hidden />
      ) : (
        <i
          className="inline-block h-[11px] w-[11px] rounded-[3px]"
          style={{ background: color }}
          aria-hidden
        />
      )}
      {label}
    </span>
  );
}

export function Legend({ children }: { children: ReactNode }) {
  return (
    <div className="text-fg-muted mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px]">
      {children}
    </div>
  );
}

const hit =
  "focus-visible:ring-accent rounded focus-visible:ring-2 focus-visible:outline-none disabled:cursor-default";

// ── What we're getting: this week vs last week, with since launch ──────────

export interface PairRow {
  id: string;
  label: string;
  a: number;
  b: number;
  total: number;
  spark: number[];
  onA?: () => void;
  onB?: () => void;
  onTotal?: () => void;
}

/**
 * Paired horizontal bars, sorted as given: this week (request blue) over last
 * week (light blue), the counts at the bar ends; then the weekly sparkline
 * since launch and its total.
 */
export function PairedBars({
  rows,
  labels,
}: {
  rows: PairRow[];
  labels: { a: string; b: string; total: string };
}) {
  const max = niceMax(Math.max(1, ...rows.map((r) => Math.max(r.a, r.b))));
  const bar = (value: number, color: string, label: string, onSelect?: () => void) => (
    <div className="flex h-[11px] items-center gap-1.5">
      <button
        type="button"
        disabled={!onSelect || value === 0}
        onClick={onSelect}
        title={label}
        aria-label={label}
        className={cn("h-full rounded-e-[4px] hover:opacity-80", hit)}
        style={{ width: `${widthPct(value, max)}%`, minWidth: value ? 3 : 0, background: color }}
      />
      <span className="text-[11.5px] leading-none font-semibold tabular-nums">{value}</span>
    </div>
  );
  return (
    <div role="list">
      {rows.map((r) => (
        <div
          key={r.id}
          role="listitem"
          className="border-border-subtle grid grid-cols-[minmax(7rem,14rem)_1fr_auto] items-center gap-3 border-t py-1.5 print:grid-cols-[12rem_1fr_9rem]"
        >
          <span className="truncate text-[13px] font-semibold" title={r.label}>
            {r.label}
          </span>
          <div className="flex flex-col gap-[3px]">
            {bar(r.a, C.request, `${r.label} · ${labels.a}: ${r.a}`, r.onA)}
            {bar(r.b, LAST_WEEK, `${r.label} · ${labels.b}: ${r.b}`, r.onB)}
          </div>
          <button
            type="button"
            onClick={r.onTotal}
            disabled={!r.onTotal}
            title={`${r.label} · ${labels.total}: ${r.total}`}
            className={cn("hover:bg-bg-subtle flex items-center gap-2 px-1", hit)}
          >
            {r.spark.length > 1 ? (
              <MiniSparkline
                data={r.spark}
                color={C.request}
                width={72}
                height={20}
                endMarker="dot"
                aria-hidden
              />
            ) : null}
            <span className="text-fg-muted min-w-8 text-end text-[12.5px] tabular-nums">
              {r.total}
            </span>
          </button>
        </div>
      ))}
    </div>
  );
}

// ── What we're getting: the newest requests as a feed ──────────────────────

export interface FeedItem {
  id: string;
  number: ReactNode;
  offering: string;
  team: string;
  ageDays: number | null | undefined;
  age: string;
}

/** The newest requests as compact cards, newest first, the age coloured as the report's ages. */
export function RequestFeed({ items }: { items: FeedItem[] }) {
  return (
    <ol className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3 print:grid-cols-3">
      {items.map((item) => {
        const bucket = ageBucket(item.ageDays);
        const color = AGE_COLORS[bucket];
        return (
          <li
            key={item.id}
            className="border-border bg-bg flex min-w-0 break-inside-avoid flex-col gap-0.5 rounded-lg border border-s-4 px-2.5 py-1.5"
            style={{ borderInlineStartColor: color }}
          >
            <div className="flex items-center justify-between gap-2 text-[12.5px]">
              <span className="font-bold">{item.number}</span>
              <span
                className={cn(
                  "rounded-full px-2 text-[11px] font-semibold tabular-nums",
                  bucket === 0 ? "text-[#1864ab]" : "text-white",
                )}
                style={{ background: color }}
              >
                {item.age}
              </span>
            </div>
            <span className="truncate text-[13px] font-semibold" title={item.offering}>
              {item.offering}
            </span>
            <span className="text-fg-muted truncate text-[12px]" title={item.team}>
              {item.team}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// ── Ranked horizontal bars (stacked parts) ─────────────────────────────────

export interface RankRow {
  id: string;
  label: string;
  parts: Part[];
  /** Shown after the total ("2.5 per person", "3 open"). */
  extra?: ReactNode;
  onLabel?: () => void;
}

/** Ranked horizontal bars, each split into its parts, the total at the end. */
export function RankBars({ rows }: { rows: RankRow[] }) {
  const totals = rows.map((r) => r.parts.reduce((s, p) => s + p.value, 0));
  const max = niceMax(Math.max(1, ...totals));
  return (
    <div role="list">
      {rows.map((r, i) => {
        const total = totals[i] ?? 0;
        return (
          <div
            key={r.id}
            role="listitem"
            className="grid grid-cols-[minmax(6rem,11rem)_1fr] items-center gap-3 py-[3px]"
          >
            <button
              type="button"
              onClick={r.onLabel}
              disabled={!r.onLabel}
              className={cn("truncate text-start text-[13px] font-semibold hover:underline", hit)}
              title={r.label}
            >
              {r.label}
            </button>
            <div className="flex items-center gap-2">
              <div
                className="flex h-[14px] gap-[2px]"
                style={{ width: `${widthPct(total, max)}%` }}
              >
                {r.parts
                  .filter((p) => p.value > 0)
                  .map((p, k, shown) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={p.onSelect}
                      disabled={!p.onSelect}
                      title={`${r.label} · ${p.label}: ${p.value}`}
                      aria-label={`${r.label} · ${p.label}: ${p.value}`}
                      className={cn(
                        "h-full hover:opacity-80",
                        k === shown.length - 1 && "rounded-e-[4px]",
                        hit,
                      )}
                      style={{ flex: p.value, background: p.color, minWidth: 3 }}
                    />
                  ))}
              </div>
              <span className="text-[12.5px] whitespace-nowrap tabular-nums">
                <b>{total}</b>
                {r.extra ? <span className="text-fg-muted"> · {r.extra}</span> : null}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** One stacked bar of parts with its key under it (e.g. "no site" by channel). */
export function MiniStack({ parts, label }: { parts: Part[]; label: string }) {
  const total = parts.reduce((s, p) => s + p.value, 0);
  const shown = parts.filter((p) => p.value > 0);
  return (
    <div>
      <div
        className="flex h-3 w-full gap-[2px] overflow-hidden rounded"
        role="img"
        aria-label={label}
      >
        {shown.map((p) => (
          <span
            key={p.id}
            title={`${p.label}: ${p.value}`}
            style={{ flex: p.value, background: p.color }}
          />
        ))}
      </div>
      <Key parts={shown} total={total} />
    </div>
  );
}

// ── Columns (a distribution) ───────────────────────────────────────────────

export interface Column {
  id: string;
  label: string;
  value: number;
  /** A line under the label ("12 tickets"). */
  sub?: string;
  onSelect?: () => void;
}

/** Vertical columns with the count on top: a distribution (people by tickets each). */
export function Columns({
  items,
  color,
  label,
}: {
  items: Column[];
  color: string;
  label: string;
}) {
  const max = niceMax(Math.max(1, ...items.map((c) => c.value)));
  return (
    <div className="flex h-48 items-end gap-3 px-1" role="group" aria-label={label}>
      {items.map((c) => (
        <div key={c.id} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end">
          <span className="mb-0.5 text-[13px] font-bold tabular-nums">{c.value}</span>
          <button
            type="button"
            onClick={c.onSelect}
            disabled={!c.onSelect}
            title={`${c.label}: ${c.value}${c.sub ? ` · ${c.sub}` : ""}`}
            aria-label={`${c.label}: ${c.value}${c.sub ? ` · ${c.sub}` : ""}`}
            className={cn("w-full max-w-16 rounded-t-[4px] hover:opacity-80", hit)}
            style={{
              height: `${widthPct(c.value, max) * 0.72}%`,
              minHeight: c.value ? 3 : 0,
              background: color,
            }}
          />
          <span className="border-border mt-0 w-full border-t pt-1 text-center text-[12.5px] font-semibold">
            {c.label}
          </span>
          {c.sub ? <span className="text-fg-muted text-center text-[11.5px]">{c.sub}</span> : null}
        </div>
      ))}
    </div>
  );
}

// ── Bars against a goal (stayed with first team) ───────────────────────────

export interface GoalRow {
  id: string;
  label: string;
  pct: number | null | undefined;
  detail: string;
  onSelect?: () => void;
}

/** Percentages on a 0–100 track with a dashed goal line; below goal says so (▼), not colour alone. */
export function GoalBars({
  rows,
  goal,
  goalLabel,
  color,
}: {
  rows: GoalRow[];
  goal: number;
  goalLabel: string;
  color: string;
}) {
  return (
    <div className="relative" role="list">
      {rows.map((r) => {
        const pct = r.pct ?? 0;
        const below = r.pct !== null && r.pct !== undefined && pct < goal;
        return (
          <div
            key={r.id}
            role="listitem"
            className="grid grid-cols-[minmax(6rem,11rem)_1fr_4.5rem] items-center gap-3 py-[3px]"
          >
            <span className="truncate text-[13px] font-semibold" title={r.label}>
              {r.label}
            </span>
            <div className="bg-bg-subtle relative h-[14px] rounded-e-[4px]">
              <button
                type="button"
                onClick={r.onSelect}
                disabled={!r.onSelect}
                title={`${r.label}: ${Math.round(pct)}% · ${r.detail}`}
                aria-label={`${r.label}: ${Math.round(pct)}% · ${r.detail}`}
                className={cn("block h-full rounded-e-[4px] hover:opacity-80", hit)}
                style={{ width: `${widthPct(pct, 100)}%`, background: color }}
              />
              <span
                className="pointer-events-none absolute -top-[3px] -bottom-[3px] border-s-2 border-dashed border-[#212529]"
                style={{ insetInlineStart: `${goal}%` }}
                aria-hidden
              />
            </div>
            <span
              className={cn(
                "text-[12.5px] font-bold whitespace-nowrap tabular-nums",
                below && "text-[#e03131]",
              )}
            >
              {r.pct === null || r.pct === undefined
                ? "–"
                : `${below ? "▼ " : ""}${Math.round(pct)}%`}
            </span>
          </div>
        );
      })}
      <div className="grid grid-cols-[minmax(6rem,11rem)_1fr_4.5rem] gap-3">
        <span />
        <div className="relative h-4">
          <span
            className="text-fg-muted absolute top-0 -translate-x-1/2 text-[11px] whitespace-nowrap rtl:translate-x-1/2"
            style={{ insetInlineStart: `${goal}%` }}
          >
            {goalLabel}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Time at a team before the move ──────────────────────────────────────────

export interface DwellRowView extends DwellLike {
  id: string;
  label: string;
  movesOut: number;
}

/**
 * Per team: the median as the bar, the middle half of the moves (25th–75th
 * percentile) as a lighter band behind it, the average as a diamond.
 */
export function DwellBars({
  rows,
  fmt,
  labels,
}: {
  rows: DwellRowView[];
  fmt: (hours: number | null | undefined) => string;
  labels: { median: string; middle: string; average: string };
}) {
  const max = dwellMax(rows);
  const at = (v: number | null | undefined) => `${widthPct(v ?? 0, max)}%`;
  return (
    <div>
      <Legend>
        <Swatch color={C.open} label={labels.median} />
        <Swatch color={SPREAD} label={labels.middle} />
        <Swatch color="#212529" label={labels.average} shape="diamond" />
      </Legend>
      <div role="list">
        {rows.map((r) => {
          const tip = `${r.label}: ${labels.median} ${fmt(r.median_hours)} · ${labels.middle} ${fmt(r.p25_hours)}–${fmt(r.p75_hours)} · ${labels.average} ${fmt(r.avg_hours)}`;
          return (
            <div
              key={r.id}
              role="listitem"
              title={tip}
              aria-label={tip}
              className="grid grid-cols-[minmax(6rem,11rem)_1fr_4.5rem] items-center gap-3 py-[3px]"
            >
              <span className="truncate text-[13px] font-semibold">{r.label}</span>
              <div className="relative h-[14px]">
                {typeof r.p25_hours === "number" && typeof r.p75_hours === "number" ? (
                  <span
                    className="absolute top-0 h-full rounded-[3px]"
                    style={{
                      insetInlineStart: at(r.p25_hours),
                      width: `calc(${at(r.p75_hours)} - ${at(r.p25_hours)} + 2px)`,
                      background: SPREAD,
                    }}
                  />
                ) : null}
                <span
                  className="absolute top-[3px] h-[8px] rounded-e-[4px]"
                  style={{ insetInlineStart: 0, width: at(r.median_hours), background: C.open }}
                />
                {typeof r.avg_hours === "number" ? (
                  <span
                    className="absolute top-[3px] h-2 w-2 -translate-x-1/2 rotate-45 rtl:translate-x-1/2"
                    style={{ insetInlineStart: at(r.avg_hours), background: "#212529" }}
                  />
                ) : null}
              </div>
              <span className="text-[12.5px] font-bold whitespace-nowrap tabular-nums">
                {fmt(r.median_hours)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── The team flow (Sankey) ──────────────────────────────────────────────────

/**
 * Team-to-team moves as a two-column flow: the teams that moved tickets on
 * the left, the teams that got them on the right, band width ∝ moves. A band
 * opens its tickets (click, or Tab to it and press Enter); hovering or
 * focusing one names the path and its count and fades the others.
 */
export function TeamFlow({
  paths,
  label,
  sides,
  onSelect,
}: {
  paths: FlowPath[];
  label: string;
  sides: { from: string; to: string };
  onSelect: (from: string, to: string) => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const teams = new Set(paths.map((p) => p.from_team)).size;
  const targets = new Set(paths.map((p) => p.to_team)).size;
  const rowsTall = Math.max(teams, targets);
  const height = Math.max(160, rowsTall * 34);
  const W = 1000;
  const x0 = 270;
  const x1 = W - 270;
  const layout = flowLayout(paths, { x0: x0 + 8, x1: x1 - 8, height, gap: 10, top: 22 });
  const H = layout.height + 8;
  const key = (from: string, to: string) => `${from}→${to}`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="mx-auto block h-auto w-full max-w-[1000px]"
      role="img"
      aria-label={label}
    >
      <text x={x0} y={12} textAnchor="end" className="fill-fg-muted text-[11px]">
        {sides.from}
      </text>
      <text x={x1} y={12} className="fill-fg-muted text-[11px]">
        {sides.to}
      </text>
      {layout.links.map((l) => {
        const id = key(l.from, l.to);
        const dim = active !== null && active !== id;
        return (
          <path
            key={id}
            d={l.d}
            fill={C.open}
            fillOpacity={active === id ? 0.6 : dim ? 0.12 : 0.32}
            stroke="var(--norican-bg, #fff)"
            strokeWidth={1}
            className="cursor-pointer focus-visible:outline-none"
            role="button"
            tabIndex={0}
            aria-label={`${l.from} → ${l.to}: ${l.value}`}
            onMouseEnter={() => setActive(id)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(id)}
            onBlur={() => setActive(null)}
            onClick={() => onSelect(l.from, l.to)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(l.from, l.to);
              }
            }}
          >
            <title>{`${l.from} → ${l.to}: ${l.value}`}</title>
          </path>
        );
      })}
      {layout.nodes.map((n) => {
        const left = n.side === "from";
        const x = left ? x0 : x1;
        return (
          <g key={n.id}>
            <rect x={left ? x : x - 8} y={n.y} width={8} height={n.h} rx={2} fill="#495057">
              <title>{`${n.team}: ${n.value}`}</title>
            </rect>
            <text
              x={left ? x - 6 : x + 6}
              y={n.y + n.h / 2 + 4}
              textAnchor={left ? "end" : "start"}
              className="fill-fg text-[12.5px] font-semibold"
            >
              {n.team.length > 30 ? `${n.team.slice(0, 29)}…` : n.team}
              <tspan className="fill-fg-muted font-normal">{` ${n.value}`}</tspan>
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function Muted({ children }: { children: ReactNode }) {
  return (
    <Typography variant="caption" tone="muted" className="block">
      {children}
    </Typography>
  );
}
